import { useAtom } from 'jotai';
import oneTrustConsentAtom from '../OneTrustAtom/oneTrustConsentAtom';
import { useEffect } from 'react';
import initTalkativeCustomConfig from '../../app/helpers/initTalkativeCustomConfig';
import { useState } from 'react';
import isKustomerChatEnabled, {
  shouldOverrideKustomerAgentStatus,
} from '../../app/helpers/isKustomerChatEnabled';
import {
  displayBlockElement,
  hideElement,
  isClient,
  setElementOpacity,
  showElement,
} from '../../app/globals';
import poll from '../../app/helpers/poll';
import { useHistory } from 'react-router-dom';
import loadKustomerScript from '../../app/helpers/loadKustomerScript';
import isTalkativeChatEnabled from '../../app/helpers/isTalkativeChatEnabled';
import loadTalkativeScript from '../../app/helpers/loadTalkativeScript';
import getEnvValue from '@joybird/joystagram-components/dist/esm/getEnvValue';
import { useCallback } from 'react';
import useOneTrustAtomHandler from '../OneTrustAtom';
import shouldDisablePopups from '../../app/helpers/shouldDisablePopups';
import useUserData from '@joybird/joystagram-components/dist/esm/useUserData';

const unloadKustomerChat = () => {
  if (isClient() && window?.Kustomer) {
    window.Kustomer.stop();
  }
};

const getFirstMessageFromTalkative = () => {
  if (isClient()) {
    const latestInteraction = window?.talkativeApi?.interaction?.getInteraction();
    const firstMessage = latestInteraction?.messages?.[0]?.message;

    return firstMessage;
  }
};

const isAmazonBot = () => {
  if (isClient()) {
    const userAgent = window?.navigator?.userAgent || '';
    // The amazon bot can have different names. amazonbot, amazonproductbot, amazonbrowserbot, etc.
    // The conditionals below should capture all versions of the user agent
    return userAgent?.includes('amazon') && userAgent?.includes('bot');
  }
  return false;
};

const ChatWidgets = () => {
  const [hasConsentOneTrust] = useAtom(oneTrustConsentAtom);
  useOneTrustAtomHandler();
  const [kustomerAgentAvailable, setKustomerAgentAvailable] = useState(false);
  const history = useHistory();
  const { isTradeUser } = useUserData();

  const initKustomerChat = useCallback(() => {
    if (isClient()) {
      window?.Kustomer.start(
        {
          brandId: getEnvValue('REACT_APP_KUSTOMER_BRANDID'),
        },
        () => {
          hideElement('#kustomer-ui-sdk-iframe');
          setElementOpacity('#kustomer-ui-sdk-iframe', 1);
          const response = window?.Kustomer?.isChatAvailable?.() ?? false;
          const availability = response.availability;
          setKustomerAgentAvailable(availability && availability === 'online');
        }
      );
    }
  }, [setKustomerAgentAvailable]);

  const onTalkativeEnterInteraction = useCallback(() => {
    if (isClient()) {
      // When an interaction starts, we want to check if this is the Customer
      // Support bot. To do this, we poll the chat for the first message, and
      // check if it contains "Launching Customer Support".
      poll({
        condition: () =>
          window?.talkativeApi &&
          getFirstMessageFromTalkative()?.includes(
            'Launching Customer Support'
          ),
        callback: () => {
          // If "Customer Support" was triggered
          window.talkativeApi.ui.hide();
          if (
            isKustomerChatEnabled() &&
            !!window?.Kustomer &&
            (kustomerAgentAvailable || shouldOverrideKustomerAgentStatus()) &&
            hasConsentOneTrust
          ) {
            hideElement('#talkative-engage');
            displayBlockElement('#kustomer-ui-sdk-iframe');
            window?.Kustomer.open();
          } else {
            // Otherwise go to the Contact Us page.

            history.push('/contact-us/');
            hideElement('#talkative-engage');
          }
        },
        interval: 50,
        maxRetries: 200,
      });
    }
  }, [kustomerAgentAvailable, hasConsentOneTrust, history]);

  const initChatIfRequired = useCallback(() => {
    if (
      isKustomerChatEnabled() &&
      !shouldDisablePopups() &&
      hasConsentOneTrust &&
      !isAmazonBot()
    ) {
      loadKustomerScript();

      window.addEventListener('kustomerLoaded', initKustomerChat);
    }

    if (isTalkativeChatEnabled() && !shouldDisablePopups()) {
      loadTalkativeScript();
    }
  }, [initKustomerChat, hasConsentOneTrust]);

  // Used to be called only on mount, but to allow for OneTrust consent state to function properly,
  // it needs to be able to run if the consent value changes
  useEffect(() => {
    initTalkativeCustomConfig({
      onTalkativeEnterInteraction,
    });
  }, [onTalkativeEnterInteraction, hasConsentOneTrust]);

  useEffect(() => {
    if (!isTradeUser) {
      initChatIfRequired();
      if (!hasConsentOneTrust) {
        unloadKustomerChat();
      }
      showElement('#talkative-engage');
    }
    return () => {
      window.removeEventListener('kustomerLoaded', initKustomerChat);
    };
  }, [hasConsentOneTrust, isTradeUser]);

  return null;
};

export default ChatWidgets;
